import { Show } from "shared/components";

import { useState, useEffect } from "react";
import { Tabs } from "shared/components/Tabs";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ActivityLog, ActivityLogsPayload, NoteData } from "../../types";
import { ActivityTabs } from "../../constants";
import { CountNotification } from "./components/CountNotification";
import { ActivityNote } from "./components/ActivityNote";
import { ActivityList } from "./components/ActivityList";
import { api } from "api/task";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

type ActivityTabProps = {
  activityLogs: ActivityLog[];
  mentionedActivityLogs?: ActivityLogsPayload;
  activityLogsLoading: boolean;
  taskId: number;
  noteId?: number;
  readOnly?: boolean;
};

const ActivityLoadingState = () => {
  return (
    <div className="flex items-center justify-center h-full">
      <span className="text-content-400">Loading activity logs...</span>
    </div>
  );
};

const ActivityEmptyState = () => {
  return (
    <div className="flex items-center justify-center h-full">
      <span className="text-content-400">No activity yet</span>
    </div>
  );
};

export const ActivityTab = ({
  activityLogs,
  mentionedActivityLogs,
  activityLogsLoading,
  taskId,
  noteId,
  readOnly = false
}: ActivityTabProps) => {
  const [currentTab, setCurrentTab] = useState<ActivityTabs>(ActivityTabs.ALL);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (noteId) setCurrentTab(ActivityTabs.MENTIONS);
  }, [noteId]);

  const { mutate: createNote, isPending: isCreatingNote } = useMutation({
    mutationKey: ["CREATE_NOTE", taskId],
    mutationFn: (payload: NoteData) =>
      api.createNote(taskId, {
        note_content: payload.note_content,
        document_html: payload.document_html,
        entities: payload.entities
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["TASK_ACTIVITY_LOGS", taskId]
      });
      queryClient.refetchQueries({
        queryKey: ["TASK_MENTION_ACTIVITY_LOGS", taskId]
      });
      toast.success("Note added successfully");
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      if (message === "Task_id is invalid")
        toast.error("You must create a task first");
      else toast.error(message);
    }
  });

  const handleAddNote = (data: NoteData) => {
    createNote(data);
  };

  return (
    <>
      <Show if={!readOnly}>
        <ActivityNote onSubmit={handleAddNote} isLoading={isCreatingNote} />
      </Show>
      <div className="flex-1 bg-background-900 overflow-y-auto rounded-bl-lg">
        <div className="px-6 py-4 sticky">
          <Tabs
            setActiveTab={(e) => setCurrentTab(e)}
            selected={currentTab}
            tabs={[ActivityTabs.ALL, ActivityTabs.MENTIONS]}
            variant="underline"
            displayMap={{
              All: "All",
              Mentions: (
                <>
                  Mentions
                  <CountNotification
                    totalCount={mentionedActivityLogs?.total_count}
                  />
                </>
              )
            }}
          />
        </div>
        <Show if={activityLogsLoading}>
          <ActivityLoadingState />
        </Show>
        <Show if={activityLogs.length > 0}>
          <ActivityList
            activityLogs={activityLogs}
            mentionedActivityLogs={mentionedActivityLogs?.items || []}
            tab={currentTab}
            noteId={noteId}
          />
        </Show>
        <Show if={activityLogs.length === 0}>
          <ActivityEmptyState />
        </Show>
      </div>
    </>
  );
};
